/**
 * SF-4729: a build's page while it waits for its transcripts (status awaiting_transcripts). The
 * wizard opens the build's page as soon as the build exists and hands over the files it chose;
 * they are sent from here, so a reload or a closed tab never strands the build. sf-api answers
 * the upload with each file read or refused, and a reason (POST .../transcripts). If every file
 * was read Eva drafts straight away; if any was refused, nothing more happens until the person
 * replaces them, carries on without them, or cancels the demo. After a reload (the files are
 * gone) the same three ways on are offered.
 */
import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Card, Form, Popconfirm, Space, Typography } from 'antd';
import {
  DEMO_BUILD_NOT_AWAITING,
  cancelArgs,
  demoErrorCode,
  demoErrorMessage,
  startDraftArgs,
  uploadTranscriptsArgs,
  useDemoMutation,
  type DemoBuild,
  type TranscriptResult,
} from '../api/demoBuildsClient';
import {
  addUploadOutcomes,
  dropHandedOverTranscripts,
  handedOverTranscripts,
  recheckTranscripts,
  useUploadOutcomes,
  type ChosenTranscript,
} from '../transcripts';
import { TranscriptOutcomes } from './TranscriptOutcomes';
import { TranscriptPicker } from './TranscriptPicker';

export interface AwaitingTranscriptsProps {
  build: DemoBuild;
  /** A POST answer for this build (or nothing): the page puts it in its cache and reads again. */
  onBuild: (buildId: string, answer?: DemoBuild) => Promise<void>;
}

const plural = (n: number) => `${n} transcript${n === 1 ? '' : 's'}`;

export const AwaitingTranscripts: React.FC<AwaitingTranscriptsProps> = ({ build, onBuild }) => {
  const buildId = build.id;
  const readSoFar = build.inputs?.transcript_ids?.length ?? 0;
  const [chosen, setFiles] = useState<ChosenTranscript[]>(() => handedOverTranscripts(buildId));
  // Checked against the places the build has left as it is now (SF-4729), so a re-read that
  // finds more transcripts read refuses the files that no longer fit.
  const files = recheckTranscripts(chosen, readSoFar);
  // Kept for the build, not this panel: they stay on the page once Eva drafts (SF-4729).
  const outcomes = useUploadOutcomes(buildId);
  const [error, setError] = useState<string | null>(null);
  const [uploadFailed, setUploadFailed] = useState(false);
  const upload = useDemoMutation<{ transcripts: TranscriptResult[] }>();
  const draft = useDemoMutation<DemoBuild>();
  const cancel = useDemoMutation<DemoBuild>();
  // From sending the files until Eva is asked: the buttons stay off between the steps too.
  const [sending, setSending] = useState(false);
  const busy = sending || draft.isLoading || cancel.isLoading;
  const handOverSent = useRef(false);

  /** Eva drafts now. A repeat call (409 DEMO_BUILD_NOT_AWAITING) means she already was asked. */
  const startDraft = async () => {
    setError(null);
    try {
      const { data } = await draft.mutateAsync(startDraftArgs(buildId));
      await onBuild(buildId, data);
    } catch (err) {
      if (demoErrorCode(err) === DEMO_BUILD_NOT_AWAITING) {
        await onBuild(buildId);
        return;
      }
      setError(`Eva could not be asked to draft this demo: ${demoErrorMessage(err, 'try again.')}`);
    }
  };

  /** Sends the files; Eva drafts only when sf-api read every one of them. */
  const send = async (list: ChosenTranscript[]) => {
    const toSend = list.filter((t) => !t.refusal);
    if (toSend.length === 0) return;
    setSending(true);
    try {
      await sendThenMaybeDraft(toSend);
    } finally {
      setSending(false);
    }
  };

  const sendThenMaybeDraft = async (toSend: ChosenTranscript[]) => {
    setError(null);
    let answer: TranscriptResult[];
    try {
      const { data } = await upload.mutateAsync(
        uploadTranscriptsArgs(
          buildId,
          toSend.map((t) => t.file),
        ),
      );
      answer = Array.isArray(data?.transcripts) ? data.transcripts : [];
    } catch (err) {
      setUploadFailed(true);
      setError(
        `The transcripts could not be sent: ${demoErrorMessage(err, 'try the upload again.')}`,
      );
      return;
    }
    setUploadFailed(false);
    setFiles([]);
    addUploadOutcomes(buildId, answer);
    const allRead = answer.length > 0 && answer.every((t) => t.status === 'read');
    // Read the build again first, so the count of transcripts read is right if the draft fails.
    await onBuild(buildId);
    if (allRead) await startDraft();
  };

  // The wizard's files go once, as soon as the page opens (a ref, so a second effect run cannot
  // send them again). Dropped from the hand-over first, so leaving and coming back never resends.
  useEffect(() => {
    if (handOverSent.current || files.length === 0) return;
    handOverSent.current = true;
    dropHandedOverTranscripts(buildId);
    void send(files);
    // Only on opening: later sends are the person's own.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCancel = async () => {
    setError(null);
    try {
      const { data } = await cancel.mutateAsync(cancelArgs(buildId));
      await onBuild(buildId, data);
    } catch (err) {
      setError(demoErrorMessage(err, 'The demo could not be cancelled.'));
    }
  };

  const refused = outcomes.filter((t) => t.status !== 'read');
  const sendable = files.some((t) => !t.refusal);
  const startLabel =
    refused.length > 0
      ? 'Carry on without them'
      : readSoFar > 0
        ? `Start drafting with the ${plural(readSoFar)} read`
        : 'Start without transcripts';

  return (
    <Card title="Transcripts">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Typography.Text>
          Eva drafts this demo once its call transcripts are in. {plural(readSoFar)} read so far.
        </Typography.Text>
        <TranscriptOutcomes outcomes={outcomes} />
        {refused.length > 0 && (
          <Alert
            type="warning"
            showIcon
            message={`${plural(refused.length)} could not be read`}
            description="Replace them with files Eva can read, carry on without them, or cancel the demo."
          />
        )}
        <Form layout="vertical">
          <TranscriptPicker
            transcripts={files}
            onTranscriptsChange={setFiles}
            locked={busy}
            pendingHint="Sent when you press Send these files"
            held={readSoFar}
          />
        </Form>
        {error && <Alert type="error" showIcon message={error} />}
        <Space wrap>
          {sendable && (
            <Button
              type="primary"
              loading={sending}
              disabled={busy}
              onClick={() => void send(files)}
            >
              {uploadFailed ? 'Try the upload again' : 'Send these files'}
            </Button>
          )}
          <Button loading={draft.isLoading} disabled={busy} onClick={() => void startDraft()}>
            {startLabel}
          </Button>
          <Popconfirm
            title="Cancel this demo?"
            description="The build stops here and nothing is made."
            okText="Yes, cancel it"
            cancelText="Keep it"
            onConfirm={() => void onCancel()}
            disabled={busy}
          >
            <Button danger disabled={busy} loading={cancel.isLoading}>
              Cancel demo
            </Button>
          </Popconfirm>
        </Space>
      </Space>
    </Card>
  );
};
